'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-dom';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { useQuickToast } from 'hooks/use-toast';
import { useFeedback } from 'providers/feedback-provider';
import { addEquipment } from 'services/equipment/actions';
import {
  DUPLICATE_EQUIPMENT_ERROR,
  FORM_FIELD_NICK_NAME,
  GENERIC_ERROR,
  NOTIFY_DEALER,
  SELECTED_EQUIPMENT,
  SHARE_EQUIPMENT_DEALER_EMAIL_ERROR
} from '../constants';
import {
  FORM_SAVE_EQUIPMENT,
  NETWORK_ERROR,
  STATUS_FAIL,
  STATUS_SUCCESS
} from 'constants/analyticsConstants';
import { capitalizeText } from '@cat-ecom/common/utils/commonUtils';
import { RegisterSignInButtonToast } from '../equipment-toast/register-signin-button-toast';
import MyEquipmentWidget from '../add-equipment/equipment-widget/my-equipment-widget';
import {
  AddEquipmentDataForm,
  AddEquipmentState,
  AddEquipmentWidget
} from 'services/equipment/types';
import styles from './add-equipment-widget.module.scss';
import { AddEquipmentHeading } from './add-equipment-heading';
import {
  MyEquipmentSaveFooter,
  EquipmentForm,
  Conditional
} from '@cat-ecom/pcc-components';
import { DrawerLoader } from '../drawer-loader';
import { getSeoUrl, normalizeCommerceLink } from 'utils/links';
import { CONTACT_DEALER_LINK } from 'constants/links';
import { useParams } from 'next/navigation';
import { DealerNotificationErrorToast } from '../equipment-toast/dealer-notification-error-toast';

const defaultFormValues = {
  serial: '',
  nickName: '',
  model: '',
  selectedEquipment: null,
  notifyDealer: false
};

const EquipmentWidgetWrapper = ({
  title,
  isLoggedIn,
  showNotifyDealer = false,
  onSuccessCallback,
  setAddToastCookie = true,
  children = null,
  storeId,
  formLocation
}: Readonly<AddEquipmentWidget>) => {
  const { updateFeedback } = useFeedback();
  const t = useTranslations();
  const toast = useQuickToast();
  const [isLoader, setIsLoader] = useState(false);
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: defaultFormValues
  });
  const { reset, watch, getValues } = formMethods;

  const watchSelectedEquipment = watch(SELECTED_EQUIPMENT);
  const { fireEvent } = useFormSubmitEvent();

  const [state, formAction] = useFormState<
    AddEquipmentState,
    AddEquipmentDataForm
  >(addEquipment, null);
  const contactUsLink = useMemo(() => {
    const pathName = `${CONTACT_DEALER_LINK}?storeId=${storeId}`;
    return normalizeCommerceLink(getSeoUrl(params, pathName));
  }, [params, storeId]);

  const fireFormSubmissionGAEvents = useCallback(
    (payload: AnalyticsEventPayload) => {
      fireEvent(payload);
    },
    [fireEvent]
  );
  const addEquipmentWithRHFProps = () =>
    formAction.bind(null, {
      selectedEquipment: getValues(SELECTED_EQUIPMENT),
      notifyDealer: getValues(NOTIFY_DEALER),
      nickName: getValues(FORM_FIELD_NICK_NAME),
      setAddToastCookie
    })();
  const showAddEquipmentToast = useCallback(
    (selectedEquipmentValues: Equipment) => {
      const {
        model = '',
        equipmentFamily = ''
      }: { equipmentFamily?: string; model?: string } = selectedEquipmentValues;
      const currentlyShoppingText = t('CURRENTLY_SHOPPING', {
        0: model,
        1: capitalizeText(equipmentFamily)
      });

      const contentGuestToast = !isLoggedIn ? (
        <RegisterSignInButtonToast
          signInText={t('SIGN_IN')}
          registerText={t('SL_REGISTER')}
          guestSaveText={t('GUEST_EQUIPMENT_SAVE')}
        />
      ) : null;

      onSuccessCallback && onSuccessCallback();
      toast.success({
        message: currentlyShoppingText,
        content: contentGuestToast,
        delay: 5
      });
    },
    [isLoggedIn, onSuccessCallback, t, toast]
  );

  const showAddEquipmentApiFailureToast = useCallback(() => {
    toast.error({
      message: t('MEQ_ADD_FAILURE_TOAST'),
      content: (
        <DealerNotificationErrorToast
          contactUsLink={contactUsLink}
          contactUsText={t('CONTACT_YOUR_DEALER')}
        />
      ),
      delay: 5
    });
  }, [contactUsLink, t, toast]);

  const showFailToSendDealerEmailToast = useCallback(() => {
    toast.error({
      message: t('MEQ_ADD_NOTIFICATION8'),
      content: (
        <DealerNotificationErrorToast
          contactUsLink={contactUsLink}
          contactUsText={t('CONTACT_YOUR_DEALER')}
        />
      ),
      delay: 5
    });
  }, [contactUsLink, t, toast]);

  useEffect(() => {
    if (!state) return;
    const selectedEquipment = getValues(SELECTED_EQUIPMENT);
    const eventContent = {
      formName: FORM_SAVE_EQUIPMENT,
      formContent: selectedEquipment.serialNumber,
      formContent2: `${selectedEquipment.model} ${selectedEquipment?.equipmentFamily}`
    };
    if (state.status === 'success') {
      showAddEquipmentToast(selectedEquipment);
      fireFormSubmissionGAEvents({
        ...eventContent,
        formStatus: STATUS_SUCCESS
      });
    }
    // this is to show duplicate Banner
    if (state.status === 'error' && 'type' in state.data) {
      if (state.data.type === DUPLICATE_EQUIPMENT_ERROR) {
        updateFeedback(DUPLICATE_EQUIPMENT_ERROR);
      } else if (state.data.type === SHARE_EQUIPMENT_DEALER_EMAIL_ERROR) {
        showFailToSendDealerEmailToast();
      } else if (state.data.type === GENERIC_ERROR) {
        showAddEquipmentApiFailureToast();
        fireFormSubmissionGAEvents({
          ...eventContent,
          formStatus: STATUS_FAIL,
          formFieldCausingError: NETWORK_ERROR
        });
      }
    }
    setIsLoader(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const onSubmitHandler = () => {
    if (getValues(SELECTED_EQUIPMENT)) {
      addEquipmentWithRHFProps();
      setIsLoader(true);
    } else {
      toast.error({
        message: t('MEQ_ADD_FAILURE_TOAST')
      });
    }
  };

  const cancelHandler = () => {
    reset(defaultFormValues);
  };

  return (
    <>
      <AddEquipmentHeading
        title={title}
        classname={styles['add-equipment__heading']}
      />
      <EquipmentForm
        formMethods={formMethods}
        handleSubmit={formMethods.handleSubmit(onSubmitHandler)}
        classname={styles['equipment-form-provider']}
      >
        <div className="position-relative row mx-0">
          <Conditional test={isLoader && !children}>
            <div className={styles['add-equipment__loader']}>
              <DrawerLoader isLoading={isLoader} />
            </div>
          </Conditional>
          <MyEquipmentWidget
            showNotifyDealer={showNotifyDealer}
            watchSelectedEquipment={watchSelectedEquipment}
            showAddEquipmentToast={showAddEquipmentToast}
            formLocation={formLocation}
          />
          {children}
          <div slot="footer" className={styles['add-equipment__footer']}>
            <MyEquipmentSaveFooter
              disableSave={watchSelectedEquipment === null}
              disableCancel={!formMethods.formState.isDirty}
              saveLabel={t('ADD')}
              cancelClass={'ps-3'}
              cancelLabel={t('DELETE_PAYMENT_METHOD_CANCEL')}
              onCancelHandler={cancelHandler}
            />
          </div>
        </div>
      </EquipmentForm>
    </>
  );
};

export default EquipmentWidgetWrapper;
